<template>
    <div class="relative w-full flex flex-col items-center text-white bg-[#0f0f0f] mt-[60px] pt-[20px] lg:pt-[30px] px-4 pb-[80px]">

        <!-- ── Coming Soon Overlay (set comingSoon: false in data() to restore) ── -->
        <div v-if="comingSoon" class="relative z-10 flex flex-col items-center justify-center min-h-[80vh] w-full text-center px-6">
          <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            <div class="absolute top-[10%] left-[-5%] w-[420px] h-[420px] rounded-full bg-[#7c3aed] opacity-[0.15] blur-[140px]"></div>
            <div class="absolute bottom-[10%] right-[-5%] w-[380px] h-[380px] rounded-full bg-[#22d3ee] opacity-[0.12] blur-[130px]"></div>
          </div>
          <div class="relative z-10 flex flex-col items-center gap-6">
            <div class="w-24 h-24 rounded-full bg-gradient-to-br from-[#7c3aed]/30 to-[#22d3ee]/20 border border-[#22d3ee]/30 flex items-center justify-center shadow-[0_0_40px_rgba(34,211,238,0.2)]">
              <svg class="w-12 h-12 text-[#22d3ee]" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
              </svg>
            </div>
            <h1 class="text-4xl sm:text-5xl lg:text-6xl font-black tracking-widest uppercase bg-gradient-to-r from-[#c6f605] via-[#22d3ee] to-[#a855f7] bg-clip-text text-transparent leading-tight">
              {{ $t('Common.ComingSoon') }}
            </h1>
            <p class="text-white/50 text-lg sm:text-xl font-semibold max-w-md">
              {{ $t('Common.WillBeAvailableSoon') }}
            </p>
            <div class="w-32 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent"></div>
            <p class="text-white/30 text-sm">{{ $t('WagerRewards.PageTitle') }}</p>
          </div>
        </div>

        <!-- ── Existing Content (hidden while comingSoon is true) ── -->
        <div v-else class="w-full">

        <!-- Color blobs -->
        <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            <div class="absolute top-[5%] left-[-5%] w-[420px] h-[420px] rounded-full bg-[#7c3aed] opacity-[0.12] blur-[130px]"></div>
            <div class="absolute top-[15%] right-[5%] w-[380px] h-[380px] rounded-full bg-[#22d3ee] opacity-[0.09] blur-[120px]"></div>
            <div class="absolute top-[40%] left-[5%] w-[440px] h-[440px] rounded-full bg-[#a855f7] opacity-[0.10] blur-[140px]"></div>
            <div class="absolute top-[70%] right-[-4%] w-[400px] h-[400px] rounded-full bg-[#4f8ef5] opacity-[0.09] blur-[120px]"></div>
            <div class="absolute top-[85%] left-[30%] w-[350px] h-[350px] rounded-full bg-[#22d3ee] opacity-[0.07] blur-[110px]"></div>
        </div>

        <div class="relative z-10 w-full flex flex-col items-center">

        <!-- HEADER BANNER -->
        <section class="w-full max-w-7xl mx-auto mb-8">
            <div class="relative w-full h-auto min-h-[120px] py-6 sm:py-0 sm:h-[120px] lg:h-[130px] overflow-hidden rounded-[20px] flex items-center bg-[#0d1225] border-x border-[#1e2535]">
                <img src="@/assets/revamp/promotion-hero-1.png" alt="" class="absolute inset-0 w-full h-full object-cover object-center pointer-events-none z-0" aria-hidden="true" />
                <div class="absolute inset-0 bg-[#060b18]/20 pointer-events-none z-[1]"></div>
                <div class="absolute inset-0 bg-gradient-to-r from-[#060b18]/85 via-[#060b18]/30 to-transparent pointer-events-none z-[1]"></div>
                <div class="relative z-[2] px-6 sm:pl-8 md:pl-10 w-full">
                    <h1 class="font-montserrat font-extrabold text-[28px] sm:text-[36px] md:text-[48px] leading-[1.1] tracking-tight text-white uppercase m-0">{{ $t('WagerRewards.Header') }}</h1>
                    <p class="text-[12px] sm:text-[14px] md:text-[15px] text-gray-300 mt-2 font-medium max-w-[90%]">{{ $t('WagerRewards.Subtext') }}</p>
                </div>
            </div>
        </section>

        <!-- INFO BAR -->
        <div class="flex flex-col sm:flex-row items-center justify-center text-center gap-2 text-gray-400 bg-[#13161e] border border-[#1e2535] px-5 py-[10px] rounded-lg text-[13px] sm:text-[14px] mb-8 w-full max-w-7xl">
            <svg xmlns="http://www.w3.org/2000/svg" class="hidden sm:flex w-5 h-5 text-[#22d3ee]" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
            </svg>
            <span v-html="$t('WagerRewards.info')"></span>
        </div>

        <!-- USER MILESTONE BAR -->
        <div class="w-full max-w-7xl bg-[#13161e] border border-[#1e2535] rounded-[16px] py-5 px-6 sm:px-10 mb-8 flex flex-col gap-5">
            <div class="flex flex-col sm:flex-row items-center justify-between gap-6">
                <!-- User info -->
                <div class="flex items-center gap-4 w-auto">
                    <img :src="getUserAvatar" alt="user avatar"
                        class="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-[#22d3ee]/40 object-cover shadow-[0_0_10px_#22d3ee33]" />
                    <div>
                        <h3 class="font-bold text-[16px] sm:text-[18px] text-white">{{ getUsername }}</h3>
                        <p class="text-gray-500 text-xs mt-0.5">{{ $t('WagerRewards.Member') }}</p>
                    </div>
                </div>

                <!-- Stats -->
                <div class="flex items-center justify-between sm:justify-start gap-2 sm:gap-8 md:gap-12 w-full sm:w-auto mt-4 sm:mt-0">
                    <div class="flex flex-col items-center text-center">
                        <span class="text-[#22d3ee] text-[10px] sm:text-[11px] uppercase font-semibold tracking-widest mb-1">{{ $t("WagerRewards.UserMilestoneBar.currentWager") }}</span>
                        <p class="text-[18px] sm:text-[22px] md:text-[26px] font-black text-white leading-none">${{ animatedCurrentWager.toLocaleString() }}</p>
                    </div>
                    <div class="w-px h-8 sm:h-10 bg-[#1e2535]"></div>
                    <div class="flex flex-col items-center text-center">
                        <span class="text-[#22d3ee] text-[10px] sm:text-[11px] uppercase font-semibold tracking-widest mb-1">{{ $t("WagerRewards.UserMilestoneBar.nextMilestone") }}</span>
                        <p class="text-[18px] sm:text-[22px] md:text-[26px] font-black text-[#22d3ee] leading-none">${{ animatedNextMilestone.toLocaleString() }}</p>
                    </div>
                    <div class="w-px h-8 sm:h-10 bg-[#1e2535]"></div>
                    <div class="flex flex-col items-center text-center">
                        <span class="text-[#22d3ee] text-[10px] sm:text-[11px] uppercase font-semibold tracking-widest mb-1">{{ $t("WagerRewards.UserMilestoneBar.progress") }}</span>
                        <p class="text-[18px] sm:text-[22px] md:text-[26px] font-black text-white leading-none">{{ animatedProgressPercent }}%</p>
                    </div>
                </div>
            </div>

        </div>

        <!-- CURRENT TIER CARD -->
        <div v-if="rewards.length" class="w-full max-w-7xl bg-[#13161e] border border-[#1e2535] rounded-[16px] py-5 px-6 sm:px-10 mb-8 flex items-center gap-6">
            <!-- Tier icon with glow + ping -->
            <div class="relative flex-shrink-0">
                <div class="w-16 h-16 rounded-xl bg-[#0d0f14] border border-[#22d3ee]/30 flex items-center justify-center shadow-[0_0_20px_#22d3ee33]">
                    <img
                        :src="getTierIcon(rewards.find(r => !r.userMilestone?.isClaimed)?.title || rewards[rewards.length - 1]?.title)"
                        alt="current tier"
                        class="w-9 h-9 object-contain"
                    />
                </div>
                <span class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#22d3ee] animate-ping opacity-40"></span>
                <span class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#22d3ee]"></span>
            </div>

            <!-- Info + mini dots -->
            <div class="flex-1 flex flex-col gap-2">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-xs text-gray-500 uppercase tracking-widest mb-0.5">{{ $t('WagerRewards.NextTierUnlock') }}</p>
                        <p class="text-white font-bold text-base leading-tight">
                            {{ rewards.find(r => !r.userMilestone?.isClaimed)?.title || 'All Complete! 🎉' }}
                        </p>
                    </div>
                    <div class="text-right">
                        <p class="text-xs text-gray-500 uppercase tracking-widest mb-0.5">{{ $t('WagerRewards.TiersClaimed') }}</p>
                        <p class="text-[#22d3ee] font-bold text-base">
                            {{ rewards.filter(r => r.userMilestone?.isClaimed).length }} <span class="text-gray-600 font-normal">/ {{ rewards.length }}</span>
                        </p>
                    </div>
                </div>
                <!-- Mini tier dots -->
                <div class="flex items-center gap-1">
                    <div
                        v-for="(tier, i) in rewards"
                        :key="i"
                        :class="[
                            'flex-1 h-1.5 rounded-full transition-all duration-300',
                            tier.userMilestone?.isClaimed
                                ? 'bg-gradient-to-r from-[#7c3aed] to-[#22d3ee]'
                                : tier.userMilestone?.status === 'Completed'
                                    ? 'bg-[#22d3ee]/40 animate-pulse'
                                    : 'bg-[#1e2535]'
                        ]"
                        :title="tier.title"
                    ></div>
                </div>
                <div class="flex justify-between text-[11px] text-gray-600">
                    <span>Bronze</span>
                    <span>Obsidian</span>
                </div>
            </div>
        </div>

        <!-- Show spinner if loading -->
        <div v-if="loading" class="flex justify-center items-center min-h-[300px] w-full">
            <div class="custom-spinner-lg"></div>
        </div>

        <!-- REWARD LIST -->
        <div v-else
            class="relative w-full max-w-7xl bg-[#13161e] border border-[#1e2535] rounded-[14px] p-4 sm:p-8">
            <div class="absolute left-[34px] sm:left-[53px] top-[70px] bottom-[40px] w-[1.5px] bg-gradient-to-b from-[#7c3aed]/60 via-[#22d3ee]/40 to-transparent"></div>

            <div v-for="(tier, index) in rewards" :key="index"
                class="relative flex items-center gap-4 sm:gap-6 mb-4 last:mb-0">
                <!-- Circle Number -->
                <div class="flex flex-col items-center justify-start flex-shrink-0 z-10">
                    <!-- Gradient border wrapper -->
                    <div
                        :class="[
                            'p-[1.5px] rounded-full transition-all duration-300',
                            tier.userMilestone?.isClaimed
                                ? 'bg-gradient-to-b from-[#FFD700] to-[#c6f605] shadow-[0_0_10px_#FFD70044]'
                                : tier.userMilestone?.status === 'Completed'
                                    ? 'bg-gradient-to-b from-[#c6f605] to-[#22d3ee] shadow-[0_0_10px_#c6f60544]'
                                    : 'bg-gradient-to-b from-[#7c3aed]/60 to-[#22d3ee]/60'
                        ]"
                    >
                        <div
                            :class="[
                                'flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-full font-bold text-[13px] sm:text-[15px] bg-[#0d0f14]',
                                tier.userMilestone?.isClaimed ? 'text-black bg-gradient-to-b from-[#FFD700] to-[#c6f605]'
                                : tier.userMilestone?.status === 'Completed' ? 'text-black bg-gradient-to-b from-[#c6f605] to-[#22d3ee]'
                                : 'text-gray-400'
                            ]"
                        >
                            <template v-if="tier.userMilestone?.isClaimed">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black"
                                    stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                                    <path d="M20 6L9 17l-5-5" />
                                </svg>
                            </template>
                            <template v-else>{{ index + 1 }}</template>
                        </div>
                    </div>
                </div>

                <!-- CARD -->
                <div
                    class="relative flex justify-between items-center flex-1 flex-wrap p-[1.5px] rounded-[12px] bg-gradient-to-r from-[#7c3aed]/40 to-[#22d3ee]/35">
                    <div
                        class="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full rounded-[10px] bg-gradient-to-r from-[#7c3aed]/[0.07] to-[#22d3ee]/[0.06] px-4 sm:px-6 py-5 gap-3">
                        <!-- LEFT SIDE -->
                        <div class="flex items-center gap-3 flex-wrap">
                            <div class="w-[40px] h-[40px] rounded-lg bg-[#0d0f14] border border-[#1e2535] flex items-center justify-center flex-shrink-0">
                                <img :src="getTierIcon(tier.title)" alt="tier icon"
                                    class="w-[28px] h-[28px] object-contain" />
                            </div>

                            <div>
                                <div class="flex items-center gap-2 mb-[2px]">
                                    <span
                                        class="text-[10px] sm:text-[11px] font-black tracking-wide uppercase px-2 py-[1px] rounded-[4px] text-white"
                                        :class="{
                                            // Bronze / Silver / Gold
                                            'bg-[#B87333]': tier.title === 'Bronze',
                                            'bg-[#8E8E8E]': tier.title === 'Silver',
                                            'bg-[#FFD700] text-black': tier.title === 'Gold',

                                            // Platinum (1–6)
                                            'bg-[#3A9ACD]': tier.title.includes('Platinum'),

                                            // ⭐ DIAMOND (GRADIENTS) – Always White Text
                                            'bg-gradient-to-r from-[#00C3FF] to-[#008BFF]':
                                                tier.title === 'Diamond I',
                                            'bg-gradient-to-r from-[#00AFFF] to-[#0078E8]':
                                                tier.title === 'Diamond II',
                                            'bg-gradient-to-r from-[#0090F0] to-[#006AC7]':
                                                tier.title === 'Diamond III',
                                            'bg-gradient-to-r from-[#0078D8] to-[#0057A8]':
                                                tier.title === 'Diamond IV',
                                            'bg-gradient-to-r from-[#0062B5] to-[#004C8F]':
                                                tier.title === 'Diamond V',

                                            // 🟣 OBSIDIAN
                                            'bg-gradient-to-r from-[#9B4CFF] to-[#6A30CC]':
                                                tier.title === 'Obsidian',
                                        }">
                                        {{ $t('Rewards.Tiers.' + tier.title.replace(' ', '')) }}
                                    </span>
                                </div>
                                <div class="flex flex-col items-start sm:items-start text-center sm:text-left">
                                    <p class="text-[12px] sm:text-[16px] text-white font-medium mb-[2px]">
                                        {{ $t('WagerRewards.Wager') }} ${{ tier.goal.toLocaleString() }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Reward (desktop) -->
                        <div
                            class="hidden mt-4 sm:static sm:flex sm:items-center sm:justify-center sm:h-full text-center sm:text-right">
                            <div class="sm:flex sm:flex-col sm:items-center sm:justify-center sm:h-full">
                                <p class="text-[11px] text-[#c6f605] font-semibold leading-none mb-1 uppercase tracking-widest">
                                    {{ $t('WagerRewards.Reward') }}
                                </p>
                                <p class="text-[18px] sm:text-[24px] font-black leading-none bg-gradient-to-r from-[#c6f605] to-[#e8ff80] bg-clip-text text-transparent">
                                    ${{ tier.reward.toLocaleString() }}
                                </p>
                            </div>
                        </div>

                        <!-- RIGHT SIDE (mobile + button) -->
                        <div
                            class="flex flex-col sm:flex-row sm:items-center sm:justify-center w-full sm:w-auto gap-1 sm:gap-5">
                            <!-- Reward (mobile) -->
                            <div
                                class="absolute right-4 top-10 sm:hidden sm:items-center sm:justify-center sm:h-full text-center sm:text-right">
                                <div class="sm:flex sm:flex-col sm:items-center sm:justify-center sm:h-full">
                                    <p
                                        class="text-[12px] sm:text-[16px] text-[#c6f605] font-semibold leading-none mb-1">
                                        {{ $t('WagerRewards.Reward') }}
                                    </p>
                                    <p
                                        class="text-[12px] sm:text-[20px] font-bold text-white leading-none sm:leading-normal">
                                        ${{ tier.reward.toLocaleString() }}
                                    </p>
                                </div>
                            </div>

                            <!-- Claim button -->
                            <button :disabled="!userToken ||
                                tier.userMilestone?.isClaimed ||
                                tier.userMilestone?.status !== 'Completed' ||
                                currentlyClaimingId === tier.userMilestone?._id
                                " @click="
                                    userToken &&
                                    tier.userMilestone?.status === 'Completed' &&
                                    !tier.userMilestone.isClaimed &&
                                    claimReward(tier)
                                    "
                                class="px-4 py-[10px] rounded-md text-[13px] sm:text-[16px] font-bold min-w-[100px] sm:min-w-[120px] flex justify-center items-center gap-2 transition-all duration-300 mt-8 sm:mt-0"
                                :class="{
                                    'bg-[#22d3ee]/10 text-[#22d3ee]/40 border border-[#22d3ee]/10 cursor-default': tier.userMilestone?.isClaimed,
                                    'bg-[#22d3ee] text-black font-bold hover:bg-[#06b6d4] shadow-[0_0_14px_#22d3ee55] active:scale-95':
                                        tier.userMilestone?.status === 'Completed' &&
                                        !tier.userMilestone?.isClaimed,
                                    'bg-[#22d3ee]/[0.06] text-[#22d3ee]/30 cursor-not-allowed border border-[#22d3ee]/10':
                                        tier.userMilestone?.status !== 'Completed' &&
                                        !tier.userMilestone?.isClaimed,
                                }">
                                {{
                                    !userToken
                                        ? $t('WagerRewards.LoginToClaim')
                                        : tier.userMilestone?.isClaimed
                                            ? $t('WagerRewards.Claimed')
                                            : tier.userMilestone?.status === "Completed"
                                                ? $t('WagerRewards.Claim')
                                                : $t('WagerRewards.Locked')
                                }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ConfirmationModal :show="showClaimSuccessModal" :title="$t('WagerRewards.ModalTitle')"
            :message="$t('WagerRewards.ModalMessage')"
            :confirmText="$t('WagerRewards.GoToDiscord')" :cancelText="$t('WagerRewards.Close')" @confirm="openDiscord" @cancel="closeClaimModal" />

        <RedirectModal :showModal="showRedirectModal" :user="$store.state.user" :isUserLoggedIn="isUserLoggedIn"
            @close="showRedirectModal = false" />

        </div><!-- end z-10 wrapper -->
    </div>
        </div><!-- end v-else -->
</template>

<script>
import axios from "axios";
import wagerRewardsLogo from "@/assets/maherco-wager-rewards.png";
import bronze from "@/assets/icons/bronze.png";
import silver from "@/assets/icons/silver.png";
import gold from "@/assets/icons/gold.png";
import platinum1 from "@/assets/icons/platinum1.png";
import platinum2 from "@/assets/icons/platinum2.png";
import platinum3 from "@/assets/icons/platinum3.png";
import platinum4 from "@/assets/icons/platinum4.png";
import platinum5 from "@/assets/icons/platinum5.png";
import platinum6 from "@/assets/icons/platinum6.png";
import diamond1 from "@/assets/icons/diamond1.png";
import diamond2 from "@/assets/icons/diamond2.png";
import diamond3 from "@/assets/icons/diamond3.png";
import diamond4 from "@/assets/icons/diamond4.png";
import diamond5 from "@/assets/icons/diamond5.png";
import obsidian from "@/assets/icons/obsidian.png";
import ConfirmationModal from "@/components/ConfirmationModal.vue";
import RedirectModal from "@/components/RedirectModal.vue";

export default {
    components: { ConfirmationModal, RedirectModal },
    data() {
        return {
            // Set to false to restore the wager rewards page
            comingSoon: true,

            loading: false,
            currentCasino: "21com",
            rewards: [],
            user: { username: "", avatar: "", totalWager: 0 },
            nextMilestone: 0,
            animatedNextMilestone: 0,
            animatedProgressPercent: 0,
            animatedCurrentWager: 0,
            progressPercent: 0,
            userToken: null,
            showRedirectModal: false,
            selectedReward: null,
            currentlyClaimingId: null,
            wagerRewardsLogo,
        };
    },
    computed: {
        isUserLoggedIn() {
            return this.$store.state.user !== null && this.$store.state.isVerified;
        },
        getUsername() {
            return this.$store.state.username || this.$store.state.user?.username || "Guest";
        },
        getUserAvatar() {
            const storeAvatar = this.$store.state.avatar;
            if (storeAvatar && storeAvatar.startsWith("http")) return storeAvatar;
            if (
                storeAvatar &&
                !storeAvatar.startsWith("http") &&
                storeAvatar !== "user avatar"
            ) {
                return `${process.env.VUE_APP_BASE_API}/uploads/${storeAvatar}`;
            }
            return new URL("@/assets/avatar-1.png", import.meta.url).href;
        },
    },
    methods: {
        async init() {
            const baseUrl = process.env.VUE_APP_BASE_API;
            const apiKey = process.env.VUE_APP_X_API_KEY;
            this.userToken = localStorage.getItem("maherco-access-token");
            this.loading = true;

            if (!this.userToken) {
                this.showRedirectModal = true;
            }

            try {
                let rewardsRes, userMilestonesRes;

                if (this.userToken) {
                    userMilestonesRes = await axios.get(`${baseUrl}/api/user/milestones`, {
                        headers: {
                            "x-api-key": apiKey,
                            Authorization: `Bearer ${this.userToken}`,
                        },
                    });

                    const milestonesGroup = userMilestonesRes.data?.milestones?.find(
                        (m) => m.leaderboardType === this.currentCasino
                    );
                    const userMilestones = milestonesGroup?.milestones || [];

                    this.rewards = userMilestones
                        .sort((a, b) => a.requiredWager - b.requiredWager)
                        .map((m, i) => ({
                            id: m._id,
                            title: this.getTierTitle(i),
                            goal: m.requiredWager,
                            reward: m.reward,
                            userMilestone: m,
                        }));

                    // ✅ Determine total wager
                    const totalData = userMilestonesRes.data?.totalWager || null;
                    if (totalData && totalData.totalWager) {
                        this.user.totalWager = totalData.totalWager;
                    } else if (Array.isArray(userMilestonesRes.data?.totalWagers)) {
                        const total = userMilestonesRes.data.totalWagers.find(
                            (w) => w.leaderboardType === this.currentCasino
                        );
                        this.user.totalWager = total?.totalWager || 0;
                    } else {
                        this.user.totalWager = 0;
                    }

                    // ✅ Auto-complete milestones locally based on current wager
                    const userTotal = this.user.totalWager;
                    this.rewards = this.rewards.map((r) => {
                        const milestone = r.userMilestone || {};
                        const isCompleted = userTotal >= r.goal;
                        if (!milestone.isClaimed) {
                            milestone.status = isCompleted ? "Completed" : "Locked";
                        }
                        return { ...r, userMilestone: milestone };
                    });
                } else {
                    // Guest fallback (no token)
                    rewardsRes = await axios.get(
                        `${baseUrl}/api/wager-rewards?type=${this.currentCasino}`,
                        { headers: { "x-api-key": apiKey } }
                    );

                    const rewardConfig = rewardsRes.data?.wagerRewards?.[0] || {};
                    const milestoneList = rewardConfig.milestones || [];

                    this.rewards = milestoneList
                        .sort((a, b) => a.requiredWager - b.requiredWager)
                        .map((m, i) => ({
                            id: m._id,
                            title: this.getTierTitle(i),
                            goal: m.requiredWager,
                            reward: m.reward,
                            userMilestone: {},
                        }));
                }

                this.calculateProgress();
                this.animateValues();
            } catch (err) {
                console.error("Error initializing wager rewards:", err.response?.data || err);
            } finally {
                this.loading = false;
            }
        },

        calculateProgress() {
            if (!this.rewards.length) return;

            const wager = this.user.totalWager;
            const completedMilestones = this.rewards.filter(
                (m) => m.userMilestone?.status === "Completed" || m.userMilestone?.isClaimed
            );

            let lastGoal = 0;
            if (completedMilestones.length) {
                lastGoal = Math.max(...completedMilestones.map((m) => m.goal));
            }

            const nextMilestoneObj = this.rewards.find((m) => m.goal > lastGoal);
            this.nextMilestone = nextMilestoneObj ? nextMilestoneObj.goal : lastGoal;

            const totalTiers = this.rewards.length;
            let progress = 0;

            if (this.nextMilestone > lastGoal) {
                const range = this.nextMilestone - lastGoal;
                const partial = Math.min((wager - lastGoal) / range, 1);
                progress =
                    (completedMilestones.length / totalTiers) * 100 + partial * (100 / totalTiers);
            } else {
                progress = 100;
            }

            this.progressPercent = Math.min(Math.round(progress), 100);
        },

        animateValues() {
            this.animateNumber("animatedCurrentWager", this.user.totalWager);
            this.animateNumber("animatedNextMilestone", this.nextMilestone);
            this.animateNumber("animatedProgressPercent", this.progressPercent);
        },

        animateNumber(variable, targetValue) {
            let start = this[variable] || 0;
            const step = Math.max(1, Math.abs(targetValue - start) / 25);
            const interval = setInterval(() => {
                if (start < targetValue) {
                    start += step;
                    if (start >= targetValue) {
                        start = targetValue;
                        clearInterval(interval);
                    }
                } else {
                    clearInterval(interval);
                }
                this[variable] = Math.round(start);
            }, 40);
        },

        claimReward(milestone) {
            if (!this.userToken) {
                this.showRedirectModal = true;
                return;
            }
            this.currentlyClaimingId = milestone.userMilestone?._id;
            this.selectedReward = milestone;
            this.showClaimSuccessModal = true;

            setTimeout(() => {
                this.currentlyClaimingId = null;
            }, 600);
        },

        getTierTitle(index) {
            const tiers = [
                "Bronze",
                "Silver",
                "Gold",
                "Platinum I",
                "Platinum II",
                "Platinum III",
                "Platinum IV",
                "Platinum V",
                "Platinum VI",
                "Diamond I",
                "Diamond II",
                "Diamond III",
                "Diamond IV",
                "Diamond V",
                "Obsidian",
            ];

            return tiers[index] || `Tier ${index + 1}`;
        },

        getTierIcon(title) {
            switch (title) {
                case "Bronze":
                    return bronze;
                case "Silver":
                    return silver;
                case "Gold":
                    return gold;

                case "Platinum I":
                    return platinum1;
                case "Platinum II":
                    return platinum2;
                case "Platinum III":
                    return platinum3;
                case "Platinum IV":
                    return platinum4;
                case "Platinum V":
                    return platinum5;
                case "Platinum VI":
                    return platinum6;

                case "Diamond I":
                    return diamond1;
                case "Diamond II":
                    return diamond2;
                case "Diamond III":
                    return diamond3;
                case "Diamond IV":
                    return diamond4;
                case "Diamond V":
                    return diamond5;

                case "Obsidian":
                    return obsidian;

                default:
                    return "";
            }
        },

        openDiscord() {
            window.open("https://discord.gg/N764VWuGyn", "_blank");
            this.closeClaimModal();
        },

        closeClaimModal() {
            this.showClaimSuccessModal = false;
            this.selectedReward = null;
            this.currentlyClaimingId = null;
        },
    },

    mounted() {
        this.init();
    },
};
</script>